/**
 * iter293 — Réglage des notifications par le destinataire d'un lien de suivi.
 * L'admin autorise depuis le CRM ; ici la personne choisit son canal, ses
 * coordonnées et les étapes qui l'intéressent — séparément pour chaque client
 * figurant sur le lien (ex. Guido et Helvetic Motion).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const PublicNotifCard = ({ token, baseUrl, acces }) => {
  const http = axios.create({
    headers: acces ? { "X-Suivi-Acces": acces } : {},
  });
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await http.get(`${baseUrl}/${token}/notifications`);
        setData(d);
        setDrafts(Object.fromEntries((d.clients || []).map((c) => [c.client_id, { ...c.prefs }])));
      } catch {
        setData({ autorise: false, clients: [] });
      }
    })();
  }, [token]);

  if (!data?.autorise || !(data.clients || []).length) return null;

  const maj = (cid, patch) => setDrafts((d) => ({ ...d, [cid]: { ...d[cid], ...patch } }));
  const majEtape = (cid, cle, val) =>
    setDrafts((d) => ({ ...d, [cid]: { ...d[cid], etapes: { ...d[cid].etapes, [cle]: val } } }));

  const enregistrer = async (cid) => {
    setBusy(cid);
    try {
      const { data: res } = await http.put(`${baseUrl}/${token}/notifications`, {
        client_id: cid, ...drafts[cid],
      });
      maj(cid, res.prefs);
      toast.success(
        res.prefs.sms || res.prefs.email ? "Notifications enregistrées" : "Notifications désactivées",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5" data-testid="public-notifications">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-[#52a675]" />
        <h2 className="text-base font-semibold text-gray-900">Être prévenu</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Choisissez comment et pour quelles étapes vous souhaitez être averti. Aucun message n&apos;est
        envoyé tant que vous n&apos;avez rien activé.
      </p>

      <div className="space-y-5">
        {data.clients.map((c) => {
          const p = drafts[c.client_id] || {};
          const actif = p.sms || p.email;
          return (
            <div
              key={c.client_id}
              className="rounded-xl border border-gray-200 p-3"
              data-testid={`public-notif-${c.client_id}`}
            >
              {data.clients.length > 1 && (
                <div className="text-sm font-semibold text-gray-900 mb-2">{c.nom}</div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox" checked={!!p.sms}
                      onChange={(e) => maj(c.client_id, { sms: e.target.checked })}
                      className="accent-[#52a675] w-4 h-4"
                      data-testid={`public-notif-sms-${c.client_id}`}
                    />
                    Par SMS
                  </label>
                  {p.sms && (
                    <input
                      value={p.tel || ""}
                      onChange={(e) => maj(c.client_id, { tel: e.target.value })}
                      placeholder="+41 79 123 45 67"
                      className="mt-1.5 w-full h-9 px-2 rounded-md border border-gray-300 text-sm"
                      data-testid={`public-notif-tel-${c.client_id}`}
                    />
                  )}
                </div>
                <div className="flex-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox" checked={!!p.email}
                      onChange={(e) => maj(c.client_id, { email: e.target.checked })}
                      className="accent-[#52a675] w-4 h-4"
                      data-testid={`public-notif-email-${c.client_id}`}
                    />
                    Par e-mail
                  </label>
                  {p.email && (
                    <input
                      value={p.email_addr || ""}
                      onChange={(e) => maj(c.client_id, { email_addr: e.target.value })}
                      placeholder="vous@exemple.ch"
                      className="mt-1.5 w-full h-9 px-2 rounded-md border border-gray-300 text-sm"
                      data-testid={`public-notif-addr-${c.client_id}`}
                    />
                  )}
                </div>
              </div>

              {actif && (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 mb-1.5">Me prévenir quand :</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {Object.entries(data.etapes).map(([cle, libelle]) => (
                      <label key={cle} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!(p.etapes || {})[cle]}
                          onChange={(e) => majEtape(c.client_id, cle, e.target.checked)}
                          className="accent-[#52a675] w-4 h-4"
                          data-testid={`public-notif-etape-${cle}-${c.client_id}`}
                        />
                        {libelle}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => enregistrer(c.client_id)}
                disabled={busy === c.client_id}
                className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#52a675] text-white text-sm font-semibold hover:bg-[#3d8a5c] disabled:opacity-60"
                data-testid={`public-notif-save-${c.client_id}`}
              >
                {busy === c.client_id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Check className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PublicNotifCard;
